import { useState } from "react";
import {
  ONBOARDING_CONTEXT_MAX_CHARS,
  type ClientTypeFamily,
  type OnboardingContext,
} from "@school-sales-ai/shared";
import { WizardFooter } from "./WizardFooter";

/**
 * Step 2 — everything the operator has about the client, pasted raw.
 *
 * Deliberately one field instead of a set of questions: the material arrives as a
 * brochure, a paste of the website or a WhatsApp thread, and splitting it into labelled
 * boxes made the operator do the extraction that step 4's model does better. The
 * placeholder keeps the guidance those questions gave — as a list of what is worth
 * hunting for, not as a form to fill.
 *
 * Ordering it is step 4's job. Nothing here has to be tidy.
 *
 * Skippable: the material is raw input, not an artifact. Without it step 4 offers the
 * segment's empty template instead of an AI draft.
 */

const PASTE_CHECKLIST: Record<ClientTypeFamily, string[]> = {
  school: [
    "Niveles y modalidades",
    "Costos: inscripción, colegiatura, materiales",
    "Requisitos de admisión y documentos",
    "Horarios y calendario",
    "Becas y descuentos",
    "Qué la distingue de otras escuelas",
    "Texto del sitio web o del folleto",
  ],
  business: [
    "Productos o servicios y cómo se eligen",
    "Precios, promociones y formas de pago",
    "Envíos, recogida o tiempos de entrega",
    "Horario y sucursales",
    "Garantías, cambios y devoluciones",
    "Qué lo distingue de la competencia",
    "Texto del sitio web, catálogo o lista de precios",
  ],
};

const PLACEHOLDER: Record<ClientTypeFamily, string> = {
  school: `Pega aquí todo lo que tengas: el texto del sitio, el folleto de admisiones, la lista de costos, un hilo de WhatsApp con el equipo…

No importa el orden ni el formato — de eso se encarga el siguiente paso.`,
  business: `Pega aquí todo lo que tengas: el texto del sitio, el catálogo, la lista de precios, un hilo de WhatsApp con el equipo de ventas…

No importa el orden ni el formato — de eso se encarga el siguiente paso.`,
};

type Props = {
  context: OnboardingContext | null;
  family: ClientTypeFamily;
  onBack: (() => void) | null;
  onDone: (context: OnboardingContext) => void;
  /** Moves on without saving material. Whatever was saved before stays. */
  onSkip: () => void;
  onDirtyChange: (dirty: boolean) => void;
  isBusy: boolean;
};

export function StepContext({
  context,
  family,
  onBack,
  onDone,
  onSkip,
  onDirtyChange,
  isBusy,
}: Props): React.JSX.Element {
  const [raw, setRaw] = useState(context?.raw ?? "");

  const length = raw.length;
  const isEmpty = raw.trim() === "";
  const isTooLong = length > ONBOARDING_CONTEXT_MAX_CHARS;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-ink-500">
        Esto es la materia prima del borrador de la base de conocimiento. Cuanto más
        concreto sea (montos, fechas, requisitos), menos huecos quedarán por llenar.
      </p>

      <div className="rounded-lg border border-ink-100 bg-white px-4 py-3">
        <p className="text-[12px] font-medium text-ink-700">Vale la pena buscar:</p>
        <ul className="mt-1.5 grid grid-cols-1 gap-x-6 gap-y-0.5 text-[12px] text-ink-500 sm:grid-cols-2">
          {PASTE_CHECKLIST[family].map((item) => (
            <li key={item}>· {item}</li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-ink-700">
          Contexto del cliente
        </label>
        <textarea
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            onDirtyChange(true);
          }}
          disabled={isBusy}
          rows={14}
          placeholder={PLACEHOLDER[family]}
          className={`rounded-md border bg-white px-3 py-2 text-sm leading-relaxed text-ink-900 disabled:opacity-50 ${
            isTooLong ? "border-red-400" : "border-ink-200"
          }`}
        />
        <div className="flex items-start justify-between gap-3">
          <span className="text-xs text-ink-400">
            El siguiente paso lo organiza en secciones y marca lo que falte como
            pendiente.
          </span>
          <span
            className={`shrink-0 text-xs ${isTooLong ? "text-red-500" : "text-ink-300"}`}
          >
            {length.toLocaleString("es-MX")}/
            {ONBOARDING_CONTEXT_MAX_CHARS.toLocaleString("es-MX")}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="max-w-lg text-[12px] text-ink-400">
          ¿Todavía no tienes material? Puedes saltar este paso: en Conocimiento empezarás
          con la plantilla vacía del tipo de cliente en lugar del borrador con IA.
        </p>
        <button
          type="button"
          onClick={onSkip}
          disabled={isBusy}
          className="text-xs font-medium text-ink-500 hover:text-moss disabled:opacity-40"
        >
          Saltar este paso
        </button>
      </div>

      <WizardFooter
        onBack={onBack}
        onContinue={() => onDone({ raw: raw.trim() })}
        canContinue={!isEmpty && !isTooLong}
        isBusy={isBusy}
        hint={
          isTooLong
            ? "El texto excede el límite. Recorta lo menos relevante."
            : isEmpty
              ? "Pega el material del cliente para continuar."
              : undefined
        }
      />
    </div>
  );
}
